import React, { useEffect, useState } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { CandidateResponse, InterviewResult } from '@/contexts/InterviewContext'
import {
  CheckCircle,
  XCircle,
  Tag,
  AlertTriangle,
  Shield,
  ShieldAlert,
  ExternalLink,
  Save as SaveIcon,
  Download,
  FileText,
  VideoOff,
} from 'lucide-react'
import { useInterview } from '@/contexts/InterviewContext'
import { Badge } from '@/components/ui/badge'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { supabase } from '@/integrations/supabase/client'
import VideoPlayer from './VideoPlayer'
import { mergeCheatEvents, getEventTypeText } from '@/utils/cheatEventUtils'
import { getStatusText } from '@/utils/status'
import { toast } from 'sonner'
import fixWebmDuration from 'webm-duration-fix'
import { pdf } from '@react-pdf/renderer'
import { useExamQuestions } from '@/hooks/useExamQuestions'
import ExamResultPDF from './ExamResultPDF'
import { PDFDownloadLink } from '@react-pdf/renderer'
import { formatLocalDate } from '@/utils/dateUtils'
import { API_BASE_URL, authedFetch } from '@/utils/apiConfig'

interface ResultSummaryProps {
  result: InterviewResult
}

type ManualEventType =
  | 'LOOKING_AWAY'
  | 'NO_FACE_DETECTED'
  | 'MULTIPLE_FACES_DETECTED'
  | 'LOOKING_AWAY_ALERT'

interface ManualEvent {
  event_type: ManualEventType
  start_offset: number
  end_offset?: number
}

const ResultSummary = ({ result }: ResultSummaryProps) => {
  const { questions } = useInterview()
  const { allQuestions, isLoading } = useExamQuestions(result)

  type AttemptVideoGroup = {
    interviewUrl: string | null
    interviewUploadFailed: boolean
    envVideos: { label: string; url: string }[]
  }
  const [videoGroups, setVideoGroups] = useState<Record<number, AttemptVideoGroup>>({})
  const [verifiedEvents, setVerifiedEvents] = useState<{ [key: string]: boolean }>({})
  const [manualAddedEvents, setManualAddedEvents] = useState<ManualEvent[]>([])
  const [savedEvents, setSavedEvents] = useState<{
    automatic: any[]
    manual: any[]
  }>({ automatic: [], manual: [] })
  const [manualType, setManualType] = useState<ManualEventType | ''>('')
  const [startTime, setStartTime] = useState('')
  const [endTime, setEndTime] = useState('')

  const VIDEO_BUCKET = 'video-recordings'

  const totalCheatEvents = React.useMemo(() => {
    const events = result.cheatingSummary?.cheatingEvents || []

    // 1. Calculate Merged Verified True
    const verifiedTrueEvents = events.filter((e) => e.verified === true)
    const mergedTrueCount = mergeCheatEvents(verifiedTrueEvents, 5.0).length

    // 2. Calculate Merged Verified Null
    const verifiedNullEvents = events.filter((e) => e.verified === null || e.verified === undefined)
    const mergedNullCount = mergeCheatEvents(verifiedNullEvents, 5.0).length

    // 3. Manual Events
    const manualCount = savedEvents.manual?.length || 0
    return mergedTrueCount + mergedNullCount + manualCount
  }, [result.cheatingSummary, savedEvents.manual])

  // Persist merged count to employee_tests.total_cheat_events
  useEffect(() => {
    if (!result?.assignmentId) return
    if (result.total_cheat_events === totalCheatEvents) return

    const persist = async () => {
      const { error } = await supabase
        .from('employee_tests')
        .update({ total_cheat_events: totalCheatEvents })
        .eq('id', result.assignmentId)

      if (error) console.error('Failed to update total_cheat_events:', error)
    }

    persist()
  }, [totalCheatEvents, result.assignmentId, result.total_cheat_events])

  useEffect(() => {
    let isMounted = true

    async function loadVideoRecords(isRetry = false) {
      try {
        const { data: records, error } = await supabase
          .from('video_records')
          .select('id, bucket_path, recording_type, attempt_number, status, upload_id, updated_at')
          .eq('employee_test_id', result.assignmentId)
          .in('status', ['completed', 'uploading', 'finalizing', 'failed'])
          .order('attempt_number', { ascending: true })
          .order('recorded_at', { ascending: true })

        if (error || !records || !isMounted) return

        // Attempt recovery for stuck uploads (only on first load, not on retry)
        if (!isRetry) {
          const TEN_MINUTES_MS = 10 * 60 * 1000
          const stuckRecords = records.filter(
            (r: any) =>
              r.recording_type === 'multi_upload' &&
              (r.status === 'uploading' || r.status === 'finalizing') &&
              r.upload_id &&
              // Only recover uploads that haven't been updated in the last 10 minutes
              // to avoid interfering with an in-progress finalization
              new Date(r.updated_at).getTime() < Date.now() - TEN_MINUTES_MS
          )
          if (stuckRecords.length > 0) {
            for (const stuck of stuckRecords) {
              try {
                const res = await authedFetch(`${API_BASE_URL}/api/interviews/complete-partial-upload`, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({
                    assignmentId: result.assignmentId,
                    employeeTestId: result.assignmentId,
                    uploadId: stuck.upload_id,
                    bucket: VIDEO_BUCKET,
                    key: stuck.bucket_path,
                    companyId: '',
                  }),
                })
                if (!res.ok)
                  console.warn('[ResultSummary] Recovery failed for upload', stuck.upload_id)
              } catch {
                /* swallow — warning badge handles the failure case */
              }
            }
            await new Promise((r) => setTimeout(r, 1500))
            if (isMounted) return loadVideoRecords(true)
            return
          }
        }

        const playbackUrls = await Promise.all((records as any[]).map(async (record) => {
          if (record.status !== 'completed') return null
          try {
            const playback = await authedFetch(
              `${API_BASE_URL}/api/video-records/${record.id}/playback-url`
            )
            if (!playback.ok) return null
            const { url } = await playback.json()
            return url || null
          } catch (error) {
            console.warn('Video playback URL request failed', error)
            return null
          }
        }))

        const groups: Record<number, AttemptVideoGroup> = {}
        for (const [index, record] of (records as any[]).entries()) {
          const n = (record.attempt_number as number) ?? 1
          if (!groups[n]) groups[n] = { interviewUrl: null, interviewUploadFailed: false, envVideos: [] }

          if (record.status !== 'completed') {
            // Mark the attempt as having an incomplete upload so a warning can be shown
            if (record.recording_type === 'multi_upload') {
              groups[n].interviewUploadFailed = true
            }
            continue
          }

          const url = playbackUrls[index]
          if (!url) continue

          if (record.recording_type === 'multi_upload') {
            groups[n].interviewUrl = url
          } else if (record.recording_type === 'environment') {
            const filename = record.bucket_path.split('/').pop() ?? ''
            let label = 'Ortam'
            if (filename === 'environment.webm') {
              label = 'Ana Ortam'
            } else if (filename.startsWith('environment_check_')) {
              const num = filename.replace('environment_check_', '').replace('.webm', '')
              label = `Ortam Kontrol ${num}`
            }
            groups[n].envVideos.push({ label, url })
          }
        }

        if (isMounted) setVideoGroups(groups)
      } catch (err) {
        if (isMounted) console.error('Video records fetch failed', err)
      }
    }

    loadVideoRecords()
    return () => {
      isMounted = false
    }
  }, [result.assignmentId])

  const handleEventVerification = (eventIds: string | string[], isVerified: boolean) => {
    const ids = Array.isArray(eventIds) ? eventIds : [eventIds]

    setVerifiedEvents((prev) => {
      const updated = { ...prev }
      ids.forEach((id) => {
        updated[id] = isVerified
      })
      return updated
    })
  }

  const handleSaveVerifications = async () => {
    try {
      // Save automatic event verifications
      const updatePromises = Object.entries(verifiedEvents).map(([eventId, isVerified]) =>
        supabase
          .from('cheat_events')
          .update({ verified: isVerified })
          .eq('id', eventId)
          .eq('employee_test_id', result.assignmentId)
      )

      await Promise.all(updatePromises)

      // Save manual events
      if (manualAddedEvents.length > 0) {
        const manualEventsToInsert = manualAddedEvents.map((event) => ({
          employee_test_id: result.assignmentId,
          event_type: event.event_type,
          start_offset: event.start_offset,
          end_offset: event.end_offset || event.start_offset,
          created_at: new Date().toISOString(),
        }))
        await supabase.from('labeled_cheat_events').insert(manualEventsToInsert)
      }

      // Refresh saved events
      const [{ data: automaticEvents }, { data: manualEvents }] = await Promise.all([
        supabase
          .from('cheat_events')
          .select('*')
          .eq('employee_test_id', result.assignmentId)
          .in('verified', [true, false]), // Keep fetching both for proper filtering
        supabase
          .from('labeled_cheat_events')
          .select('*')
          .eq('employee_test_id', result.assignmentId),
      ])

      setSavedEvents({
        automatic: automaticEvents || [],
        manual: manualEvents || [],
      })

      // Clear states after successful save
      setVerifiedEvents({})
      setManualAddedEvents([])
      toast.success('Değişiklikler kaydedildi.')
    } catch (error) {
      console.error('Error saving changes:', error)
      toast.error('Değişiklikler kaydedilemedi.')
    }
  }

  const handleAddManualEvent = () => {
    if (!manualType || !startTime) return

    const startSeconds = startTime.split(':').reduce((acc, time) => 60 * acc + +time, 0)
    const endSeconds = endTime
      ? endTime.split(':').reduce((acc, time) => 60 * acc + +time, 0)
      : undefined

    setManualAddedEvents((prev) => [
      ...prev,
      {
        event_type: manualType,
        start_offset: startSeconds,
        end_offset: endSeconds,
      },
    ])

    // Reset form
    setManualType('')
    setStartTime('')
    setEndTime('')
  }

  useEffect(() => {
    const fetchSavedEvents = async () => {
      try {
        // Fetch both verified events
        const { data: automaticEvents, error: autoError } = await supabase
          .from('cheat_events')
          .select('*')
          .eq('employee_test_id', result.assignmentId)
          .in('verified', [true, false])

        if (autoError) throw autoError

        const { data: manualEvents, error: manualError } = await supabase
          .from('labeled_cheat_events')
          .select('*')
          .eq('employee_test_id', result.assignmentId)

        if (manualError) throw manualError

        const newSavedEvents = {
          automatic: automaticEvents || [],
          manual: manualEvents || [],
        }

        setSavedEvents(newSavedEvents)
      } catch (error) {
        console.error('Error fetching saved events:', error)
        setSavedEvents({ automatic: [], manual: [] })
      }
    }

    fetchSavedEvents()
  }, [result.assignmentId])

  const getOptionText = (questionId: string, optionId: string) => {
    const question = questions.find((q) => q.id === questionId)
    if (!question) return optionId

    const option = question.options.find((o) => o.id.toLowerCase() === optionId.toLowerCase())
    return option ? `${option.id.toUpperCase()}: ${option.text}` : optionId
  }

  const getCorrectAnswerText = (questionId: string) => {
    const question = questions.find((q) => q.id === questionId)
    if (!question || !question.correctAnswerId) return 'Belirtilmemiş'

    const correctOption = question.options.find(
      (o) => o.id.toLowerCase() === question.correctAnswerId?.toLowerCase()
    )
    return correctOption
      ? `${correctOption.id.toUpperCase()}: ${correctOption.text}`
      : question.correctAnswerId
  }

  const getQuestionCategory = (questionId: string) => {
    const question = questions.find((q) => q.id === questionId)
    return question?.category || 'Genel'
  }

  function formatOffsetToMMSS(offsetInSeconds) {
    if (typeof offsetInSeconds !== 'number' || isNaN(offsetInSeconds)) {
      return '00:00' // Or some other default/error string
    }

    const totalSeconds = Math.floor(offsetInSeconds)
    const minutes = Math.floor(totalSeconds / 60)
    const seconds = totalSeconds % 60

    // Pad with leading zeros if necessary
    const paddedMinutes = String(minutes).padStart(2, '0')
    const paddedSeconds = String(seconds).padStart(2, '0')

    return `${paddedMinutes}:${paddedSeconds}`
  }

  const shouldShowEvent = (event: any): boolean => {
    const allIdsProcessed = event.ids.every((id) =>
      savedEvents.automatic?.some((saved) => saved.id === id)
    )
    return !allIdsProcessed
  }

  const getVerifiedMergedEvents = () => {
    if (!result.cheatingSummary?.cheatingEvents) return []

    const allMerged = mergeCheatEvents(result.cheatingSummary.cheatingEvents, 5.0)
    return allMerged.filter((event) =>
      event.ids.some((id) =>
        savedEvents.automatic?.some((saved) => saved.id === id && saved.verified === true)
      )
    )
  }

  const downloadVideo = async (url: string, filename: string) => {
    try {
      // Fetch the video file
      const response = await fetch(url, {
        mode: 'cors',
        credentials: 'omit',
      })
      if (!response.ok) throw new Error(`HTTP ${response.status}`)

      const originalBlob = await response.blob()

      // Fix WebM duration issues
      const fixedBlob = await fixWebmDuration(originalBlob)

      // Create object URL from the fixed blob
      const blobUrl = window.URL.createObjectURL(fixedBlob)

      // Create and click download link
      const link = document.createElement('a')
      link.href = blobUrl
      link.download = filename
      document.body.appendChild(link)
      link.click()

      // Cleanup
      document.body.removeChild(link)
      window.URL.revokeObjectURL(blobUrl)
    } catch (error) {
      console.error('Download failed:', error)
      // Fallback to opening in new tab
      window.open(url, '_blank')
    }
  }

  const handlePDFDownload = async () => {
    // Create a blob from the PDF and trigger download
    const blob = await pdf(<ExamResultPDF result={result} allQuestions={allQuestions} />).toBlob()

    // Create download link
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `${result.candidateName}_Sinav_Raporu.pdf`
    document.body.appendChild(link)
    link.click()

    // Cleanup
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
  }

  const attemptNumbers = Object.keys(videoGroups).map(Number).sort((a, b) => a - b)
  const totalAttempts = result.attempt_count ?? 1
  const allAttemptNumbers = Array.from({ length: totalAttempts }, (_, i) => i + 1)
  const hasInterviewVideos = attemptNumbers.some((n) => videoGroups[n].interviewUrl !== null)
  const hasIncompleteUploads = attemptNumbers.some(
    (n) => videoGroups[n].interviewUploadFailed && !videoGroups[n].interviewUrl
  )
  const hasEnvVideos = attemptNumbers.some((n) => videoGroups[n].envVideos.length > 0)
  const multipleAttempts = totalAttempts > 1

  const isExpired = result.status === 'expired'

  return (
    <Card className="w-full shadow-md">
      <CardHeader className="bg-gray-50 border-b pb-6">
        <CardTitle className="flex justify-between items-start">
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="text-base px-3 py-1 -mt-2">
              {getStatusText(result.status)}
            </Badge>
          </div>
          <span className="text-sm font-normal text-gray-500">
            {' '}
            {formatLocalDate(result.assignmentDueDate || result.timestamp)}
          </span>
        </CardTitle>
        {!isExpired && (
          <CardDescription className="mt-8 ml-4">
            Toplam Doğru: {result.totalCorrect} / {result.totalQuestions}
          </CardDescription>
        )}

        <div className="mt-2 flex w-full gap-2">
          {hasEnvVideos && (
            <Dialog>
              <DialogTrigger asChild>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="flex-1 flex items-center justify-center gap-1 text-green-600"
                >
                  360° Ortam Videosu
                  <ExternalLink className="h-4 w-4" />
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl">
                <DialogHeader>
                  <DialogTitle>360° Ortam Videosu</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  {attemptNumbers.map((n) => {
                    const { envVideos } = videoGroups[n]
                    if (envVideos.length === 0) return null
                    return (
                      <div key={n}>
                        {multipleAttempts && (
                          <h3 className="text-sm font-semibold text-gray-500 mb-2">Deneme {n}</h3>
                        )}
                        <div className="space-y-3">
                          {envVideos.map(({ label, url }) => (
                            <div
                              key={label}
                              className="block p-4 border rounded-lg hover:bg-gray-50 transition-colors group"
                            >
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                  <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center group-hover:bg-green-200 transition-colors">
                                    <ExternalLink className="h-5 w-5 text-green-600" />
                                  </div>
                                  <div>
                                    <p className="font-medium text-gray-900">{label}</p>
                                    <p className="text-sm text-gray-500">Tıklayarak görüntüleyin</p>
                                  </div>
                                </div>
                                <div className="flex items-center gap-2">
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="text-gray-400 hover:text-gray-600"
                                    onClick={(e) => {
                                      e.preventDefault()
                                      e.stopPropagation()
                                      downloadVideo(url, `${label.toLowerCase().replace(/\s+/g, '_')}.webm`)
                                    }}
                                  >
                                    <Download className="h-5 w-5" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="text-gray-400 hover:text-gray-600"
                                    onClick={(e) => {
                                      e.preventDefault()
                                      e.stopPropagation()
                                      window.open(url, '_blank')
                                    }}
                                  >
                                    <ExternalLink className="h-5 w-5" />
                                  </Button>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )
                  })}
                </div>
              </DialogContent>
            </Dialog>
          )}

          {(hasInterviewVideos || hasIncompleteUploads || totalAttempts > 1) && (
            <Dialog>
              <DialogTrigger asChild>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="flex-1 flex items-center justify-center gap-1 text-blue-600 hover:bg-blue-50"
                >
                  Sınav Videosunu Görüntüle
                  <ExternalLink className="h-4 w-4" />
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-7xl">
                <DialogHeader className="border-b pb-4">
                  <DialogTitle className="text-xl">Sınav Videosu ve Kopya Kontrol</DialogTitle>
                </DialogHeader>
                <div className="grid grid-cols-2 gap-6 pt-4">
                  {/* Video Player Column */}
                  <div className="col-span-1 space-y-4 max-h-[80vh] overflow-y-auto">
                    {allAttemptNumbers.map((n) => {
                      const group = videoGroups[n]
                      const interviewUrl = group?.interviewUrl ?? null
                      const interviewUploadFailed = group?.interviewUploadFailed ?? false
                      return (
                        <div key={n}>
                          {multipleAttempts && (
                            <p className="text-sm font-semibold text-gray-500 mb-1">Deneme {n}</p>
                          )}
                          {interviewUrl ? (
                            <div className="rounded-lg overflow-hidden border shadow-sm">
                              <VideoPlayer src={interviewUrl} />
                            </div>
                          ) : interviewUploadFailed ? (
                            <div className="rounded-lg border bg-amber-50 p-4 flex items-center gap-2 text-amber-700">
                              <AlertTriangle className="h-5 w-5 flex-shrink-0" />
                              <span className="text-sm">Sınav videosu yüklenemedi</span>
                            </div>
                          ) : (
                            <div className="rounded-lg border bg-gray-50 p-4 flex items-center gap-2 text-gray-400">
                              <VideoOff className="h-5 w-5 flex-shrink-0" />
                              <span className="text-sm">Video kaydedilmedi</span>
                            </div>
                          )}
                        </div>
                      )
                    })}
                  </div>

                  {/* Events Column */}
                  <div className="col-span-1 max-h-[80vh] overflow-y-auto rounded-lg border bg-white p-6">
                    {/* Header */}
                    <div className="top-0 z-10 bg-white pb-4 mb-6 border-b">
                      <h3 className="flex items-center gap-2 font-semibold text-lg">
                        <ShieldAlert className="h-6 w-6 text-orange-500" />
                        Kopya Olayları Etiketleme
                      </h3>
                    </div>
                    {result.cheatingSummary?.cheatingEvents.length === 0 ? (
                      <div className="text-center py-12 bg-gray-50 rounded-lg">
                        <Shield className="h-12 w-12 mx-auto mb-3 text-green-500" />
                        <p className="text-gray-600 font-medium">Şüpheli olay tespit edilmedi</p>
                      </div>
                    ) : (
                      <div className="space-y-8">
                        {/* Verified Events Section - show merged events */}
                        {(getVerifiedMergedEvents().length > 0 ||
                          savedEvents.manual?.length > 0) && (
                          <div>
                            <h4 className="font-medium mb-3 flex items-center gap-2">
                              <CheckCircle className="h-4 w-4 text-green-600" />
                              Doğrulanmış Olaylar
                            </h4>
                            <div className="space-y-2">
                              {getVerifiedMergedEvents().map((event, index) => (
                                <div
                                  key={`verified-merged-${index}`}
                                  className="flex items-center justify-between p-3 bg-green-50 rounded-lg"
                                >
                                  <span className="flex items-center gap-2">
                                    <CheckCircle className="h-4 w-4 text-green-600" />
                                    <span className="text-sm font-medium">
                                      {getEventTypeText(event.type)}
                                    </span>
                                  </span>
                                  <span className="text-sm text-gray-500">
                                    {event.count === 1
                                      ? formatOffsetToMMSS(event.startOffset)
                                      : `${formatOffsetToMMSS(event.startOffset)} - ${formatOffsetToMMSS(event.endOffset)}`}
                                  </span>
                                </div>
                              ))}

                              {/* Verified Manual Events */}
                              {savedEvents.manual?.map((event, index) => (
                                <div
                                  key={`verified-manual-${index}`}
                                  className="flex items-center justify-between p-3 bg-blue-50 rounded-lg"
                                >
                                  <span className="flex items-center gap-2">
                                    <Tag className="h-4 w-4 text-blue-600" />
                                    <span className="text-sm font-medium">
                                      {getEventTypeText(event.event_type)}
                                    </span>
                                  </span>
                                  <span className="text-sm text-gray-500">
                                    {formatOffsetToMMSS(event.start_offset)}
                                    {event.end_offset &&
                                      ` - ${formatOffsetToMMSS(event.end_offset)}`}
                                  </span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Automatic Events Section */}
                        <section className="space-y-3">
                          <h4 className="font-medium text-gray-900 flex items-center gap-2">
                            <AlertTriangle className="h-4 w-4 text-yellow-500" />
                            Otomatik Tespit Edilen Olaylar
                          </h4>
                          <div className="space-y-2">
                            {result.cheatingSummary?.cheatingEvents &&
                              mergeCheatEvents(result.cheatingSummary.cheatingEvents, 5.0)
                                .filter(shouldShowEvent)
                                .map((event, index) => (
                                  <div
                                    key={index}
                                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                                  >
                                    <span className="flex items-center gap-2">
                                      <AlertTriangle className="h-4 w-4 text-yellow-500" />
                                      <span className="text-sm font-medium">
                                        {getEventTypeText(event.type)}
                                      </span>
                                    </span>
                                    <div className="flex items-center gap-3">
                                      <span className="text-sm text-gray-500">
                                        {event.count === 1
                                          ? formatOffsetToMMSS(event.startOffset)
                                          : `${formatOffsetToMMSS(event.startOffset)} - ${formatOffsetToMMSS(event.endOffset)}`}
                                      </span>
                                      <div className="flex items-center gap-2">
                                        <Button
                                          size="sm"
                                          variant={
                                            event.ids.every((id) => verifiedEvents[id] === true)
                                              ? 'default'
                                              : 'outline'
                                          }
                                          className={
                                            event.ids.every((id) => verifiedEvents[id] === true)
                                              ? 'bg-green-600 hover:bg-green-700 text-white'
                                              : 'hover:bg-green-50 hover:text-green-700 hover:border-green-300'
                                          }
                                          onClick={() => handleEventVerification(event.ids, true)}
                                        >
                                          Evet
                                        </Button>
                                        <Button
                                          size="sm"
                                          variant={
                                            event.ids.every((id) => verifiedEvents[id] === false)
                                              ? 'destructive'
                                              : 'outline'
                                          }
                                          className={
                                            event.ids.every((id) => verifiedEvents[id] === false)
                                              ? 'bg-red-600 hover:bg-red-700 text-white'
                                              : 'hover:bg-red-50 hover:text-red-700 hover:border-red-300'
                                          }
                                          onClick={() => handleEventVerification(event.ids, false)}
                                        >
                                          Hayır
                                        </Button>
                                      </div>
                                    </div>
                                  </div>
                                ))}
                          </div>
                        </section>

                        {/* Manual Events Section */}
                        <section className="space-y-4 bg-gray-50 rounded-lg p-4">
                          <h4 className="font-medium text-gray-900 flex items-center gap-2">
                            <Tag className="h-4 w-4 text-blue-600" />
                            Manuel Olay Ekleme
                          </h4>
                          <div className="space-y-3">
                            <select
                              value={manualType}
                              onChange={(e) => setManualType(e.target.value as ManualEventType)}
                              className="w-full px-3 py-2 text-sm border rounded-lg bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            >
                              <option value="">Olay türü seçin</option>
                              <option value="LOOKING_AWAY">Başka Yöne Bakma</option>
                              <option value="NO_FACE_DETECTED">Yüz Tespit Edilemedi</option>
                              <option value="MULTIPLE_FACES_DETECTED">
                                Birden Fazla Yüz Tespit Edildi
                              </option>
                              <option value="LOOKING_AWAY_ALERT">Uzun Süre Başka Yöne Bakma</option>
                            </select>
                            <div className="grid grid-cols-2 gap-3">
                              <input
                                type="text"
                                value={startTime}
                                onChange={(e) => setStartTime(e.target.value)}
                                placeholder="Başlangıç (dd:ss)"
                                className="px-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                              />
                              <input
                                type="text"
                                value={endTime}
                                onChange={(e) => setEndTime(e.target.value)}
                                placeholder="Bitiş (opsiyonel dd:ss)"
                                className="px-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                              />
                            </div>
                            <Button
                              onClick={handleAddManualEvent}
                              className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                            >
                              Manuel Olay Ekle
                            </Button>
                          </div>
                        </section>

                        {(Object.keys(verifiedEvents).length > 0 ||
                          manualAddedEvents.length > 0) && (
                          <div className="pt-4 bg-white border-t">
                            <Button
                              onClick={handleSaveVerifications}
                              className="w-full bg-blue-600 hover:bg-blue-700 text-white flex items-center justify-center gap-2"
                            >
                              <SaveIcon className="h-5 w-5" />
                              Değişiklikleri Kaydet
                            </Button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          )}

          <Button
            variant="outline"
            size="sm"
            onClick={handlePDFDownload}
            className="flex items-center gap-2 text-red-600 hover:bg-red-50"
          >
            <Download className="h-4 w-4" />
            PDF
          </Button>
        </div>
      </CardHeader>
      <CardContent className="p-6">
        {!isExpired && result.cheatingSummary && (
          <Dialog>
            <DialogTrigger asChild>
              <div className="mb-6 p-4 border rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                <div className="flex items-center gap-2">
                  {result.cheatingSummary.cheatingDetected ? (
                    <>
                      <ShieldAlert className="h-5 w-5 text-red-500" />
                      <span className="font-medium text-red-500">Kopya Şüphesi Tespit Edildi</span>
                    </>
                  ) : (
                    <>
                      <Shield className="h-5 w-5 text-green-500" />
                      <span className="font-medium text-green-500">
                        Kopya Şüphesi Tespit Edilmedi
                      </span>
                    </>
                  )}
                  <Badge variant="outline" className="ml-auto">
                    Detayları Görüntüle
                  </Badge>
                </div>
              </div>
            </DialogTrigger>
            <DialogContent className="max-h-[75vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Kopya Kontrol Detayları</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div className="border-b pb-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-gray-500">Sekme Değiştirme İhlalleri:</span>
                    <span className="font-medium">{result.cheatingSummary.tabViolations}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-500">Genel Durum:</span>
                    <Badge
                      variant={result.cheatingSummary.cheatingDetected ? 'destructive' : 'outline'}
                    >
                      {result.cheatingSummary.cheatingDetected ? 'Şüpheli' : 'Temiz'}
                    </Badge>
                  </div>
                </div>

                {result.cheatingSummary.cheatingEvents &&
                  result.cheatingSummary.cheatingEvents.length > 0 && (
                    <div>
                      <h4 className="font-medium mb-3 flex items-center gap-2">
                        <CheckCircle className="h-4 w-4 text-green-600" />
                        Kopya Şüphesi
                      </h4>
                      <div className="space-y-4">
                        {/* Display verified: true events */}
                        {result.cheatingSummary.cheatingEvents.filter(
                          (event) => event.verified === true
                        ).length > 0 && (
                          <div>
                            <h5 className="text-sm font-medium mb-2 text-green-700">
                              Doğrulanmış Olaylar:
                            </h5>
                            <div className="space-y-1">
                              {mergeCheatEvents(
                                result.cheatingSummary.cheatingEvents.filter(
                                  (event) => event.verified === true
                                ),
                                5.0
                              ).map((event, index) => (
                                <div
                                  key={`verified-true-${index}`}
                                  className="flex items-center justify-between p-2 bg-green-50 rounded text-xs"
                                >
                                  <span>{getEventTypeText(event.type)}</span>
                                  <span className="text-gray-500">
                                    {event.count === 1
                                      ? formatOffsetToMMSS(event.startOffset)
                                      : `${formatOffsetToMMSS(event.startOffset)} - ${formatOffsetToMMSS(event.endOffset)}`}
                                  </span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Display verified: null events */}
                        {result.cheatingSummary.cheatingEvents.filter(
                          (event) => event.verified === null || event.verified === undefined
                        ).length > 0 && (
                          <div>
                            <div className="space-y-1">
                              {mergeCheatEvents(
                                result.cheatingSummary.cheatingEvents.filter(
                                  (event) => event.verified === null || event.verified === undefined
                                ),
                                5.0
                              ).map((event, index) => (
                                <div
                                  key={`verified-null-${index}`}
                                  className="flex items-center justify-between p-2 bg-gray-50 rounded"
                                >
                                  <span>{getEventTypeText(event.type)}</span>
                                  <span className="text-gray-500">
                                    {event.count === 1
                                      ? formatOffsetToMMSS(event.startOffset)
                                      : `${formatOffsetToMMSS(event.startOffset)} - ${formatOffsetToMMSS(event.endOffset)}`}
                                  </span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        {/* Verified Manual Events */}
                        {savedEvents.manual?.map((event, index) => (
                          <div
                            key={`verified-manual-${index}`}
                            className="flex items-center justify-between p-3 bg-blue-50 rounded-lg"
                          >
                            <span className="flex items-center gap-2">
                              <Tag className="h-4 w-4 text-blue-600" />
                              <span className="text-sm font-medium">
                                {getEventTypeText(event.event_type)}
                              </span>
                            </span>
                            <span className="text-sm text-gray-500">
                              {formatOffsetToMMSS(event.start_offset)}
                              {event.end_offset && ` - ${formatOffsetToMMSS(event.end_offset)}`}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
              </div>
            </DialogContent>
          </Dialog>
        )}
        {!isExpired &&
          allQuestions.map((question) => {
            const response = result.responses.find((r) => r.questionId === question.id)
            const category = getQuestionCategory(question.id)

            // Find the correct answer from allChoices
            const correctChoice = question.allChoices?.find((choice) => choice.isCorrect === true)
            const selectedChoice = question.allChoices?.find((choice) => choice.isSelected === true)

            return (
              <div key={question.id} className="border rounded-md p-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex-1">
                    <div
                      dangerouslySetInnerHTML={{ __html: question.questionText }}
                      className="prose prose-sm max-w-none prose-headings:my-2 prose-p:my-1 prose-ol:my-2 prose-ul:my-2 prose-li:my-0.5 mb-4"
                    />
                    {category && category !== 'Genel' && (
                      <Badge variant="secondary" className="text-xs">
                        {category}
                      </Badge>
                    )}
                  </div>
                  {response || selectedChoice ? (
                    response?.isCorrect || selectedChoice?.isCorrect ? (
                      <CheckCircle className="h-5 w-5 text-green-500 ml-4" />
                    ) : (
                      <XCircle className="h-5 w-5 text-red-500 ml-4" />
                    )
                  ) : (
                    <Badge variant="outline" className="text-gray-500 ml-4">
                      Cevaplanmadı
                    </Badge>
                  )}
                </div>

                {response || selectedChoice ? (
                  <>
                    <div className="bg-gray-50 p-2 rounded-md mt-2">
                      <span className="font-medium">Yanıt:</span>{' '}
                      {response?.selectedText || selectedChoice?.text || 'Belirtilmemiş'}
                    </div>

                    {(response?.isCorrect === false ||
                      (selectedChoice && !selectedChoice.isCorrect)) &&
                      correctChoice && (
                        <div className="bg-green-50 p-2 rounded-md mt-2">
                          <span className="font-medium text-green-700">Doğru Yanıt:</span>{' '}
                          {correctChoice.text}
                        </div>
                      )}
                  </>
                ) : (
                  <>
                    <div className="bg-gray-50 p-2 rounded-md mt-2 text-gray-500 italic">
                      Bu soru cevaplanmadı
                    </div>
                    {correctChoice && (
                      <div className="bg-green-50 p-2 rounded-md mt-2">
                        <span className="font-medium text-green-700">Doğru Yanıt:</span>{' '}
                        {correctChoice.text}
                      </div>
                    )}
                  </>
                )}
              </div>
            )
          })}
      </CardContent>
    </Card>
  )
}

export default ResultSummary
